import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Users,
  FileText,
  TrendingUp,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import { bondTrusteesAPI, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

const TrusteeDashboard = () => {
  const navigate = useNavigate();
  const { user, getDisplayName } = useAuth();

  // Fetch assigned bonds for the current trustee
  const { data: assignmentsData, isPending: bondsLoading } = useQuery({
    queryKey: ['trustee-bonds', user?.id],
    queryFn: () => bondTrusteesAPI.getTrusteeBonds(user.id || user._id),
    enabled: !!user,
    select: (response) => {
      const assignments = response?.data?.data || response?.data || [];
      const assignmentsArray = Array.isArray(assignments) ? assignments : [];
      return assignmentsArray;
    },
  });

  const isLoading = bondsLoading;
  const assignments = assignmentsData || [];
  const bonds = assignments.map(assignment => assignment.bondId).filter(Boolean);

  // Calculate statistics
  const totalBonds = bonds.length;
  const activeBonds = bonds.filter(b => b.status === 'Active' || b.status === 'active').length;
  const totalHolders = bonds.reduce((sum, bond) => sum + (bond.tokenHolders || 0), 0);

  const keyMetrics = [
    {
      name: 'Assigned Bonds',
      value: totalBonds.toString(),
      icon: Building2,
      color: 'blue',
      href: '/trustee/bonds',
    },
    {
      name: 'Active Bonds',
      value: activeBonds.toString(),
      icon: TrendingUp,
      color: 'green',
      href: '/trustee/bonds?status=active',
    },
    {
      name: 'Total Bond Holders',
      value: totalHolders.toString(),
      icon: Users,
      color: 'purple',
      href: '/trustee/holders',
    },
    {
      name: 'Recent Activity',
      value: assignments.length.toString(),
      icon: FileText,
      color: 'orange',
      href: '/trustee/bonds',
    },
  ];

  const recentBonds = bonds.slice(0, 5);

  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading Trustee Dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Trustee Dashboard</h1>
              <p className="text-sm text-gray-500">
                Welcome back, {getDisplayName()}! Here's an overview of your assigned bonds.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {keyMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div
                key={metric.name}
                onClick={() => metric.href && navigate(metric.href)}
                className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{metric.value}</p>
                  </div>
                  <div className={`p-3 rounded-full bg-${metric.color}-100`}>
                    <Icon className={`h-6 w-6 text-${metric.color}-600`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Assigned Bonds */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Assigned Bonds</h2>
          </div>
          <div className="overflow-x-auto">
            {recentBonds.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No bonds assigned yet</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bond Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bond Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Holders
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Maturity Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentBonds.map((bond) => (
                    <tr key={bond._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{bond.bondName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{bond.bondNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bond.status === 'Active' || bond.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                          {bond.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {bond.tokenHolders || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {bond.maturityDate ? formatDate(bond.maturityDate) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => navigate(`/trustee/bonds/${bond._id}`)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {recentBonds.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => navigate('/trustee/bonds')}
                className="text-sm text-blue-600 hover:text-blue-900 font-medium"
              >
                View all bonds →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrusteeDashboard;

